import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { TCity, TJurisdiction, TProvince } from "types/dto";
import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { TResponseCode } from "utils/netCall/types";

export type TRegionData = {
  province?: TProvince;
  city?: TCity;
  jurisdiction?: TJurisdiction;
};

export function useRegionsData() {
  const [provinces, setProvinces] = useState<TProvince[]>([]);
  const [isProvincesLoading, setIsProvincesLoading] = useState(true);
  const [cities, setCities] = useState<TCity[]>([]);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [jurisdictions, setJurisdictions] = useState<TJurisdiction[]>([]);
  const [isJurisdictionsLoading, setIsJurisdictionsLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<TRegionData>({
    city: undefined,
    jurisdiction: undefined,
    province: undefined,
  });

  const getProvincesData = useCallback(async () => {
    setIsProvincesLoading(true);
    const provincesData = await getAllProvinceWithChild();
    if (provincesData) {
      setProvinces(provincesData);
    }
    setIsProvincesLoading(false);
  }, []);

  const getCitiesData = useCallback(
    async (provinceId: string) => {
      setIsCitiesLoading(true);
      const citiesData =
        provinces.find((p) => p.id === provinceId)?.cities || [];
      if (citiesData) {
        setCities(citiesData);
      }
      setIsCitiesLoading(false);
    },
    [provinces]
  );

  const getJurisdictionsData = useCallback(
    async (cityId: string) => {
      setIsJurisdictionsLoading(true);
      const jurisdictionsData =
        cities.find((c) => c.id === cityId)?.jurisdictions || [];
      if (jurisdictionsData) {
        setJurisdictions(jurisdictionsData);
      }
      setIsJurisdictionsLoading(false);
    },
    [cities]
  );

  useEffect(() => {
    getProvincesData();
  }, []);

  useEffect(() => {
    if (selectedRegion.province) {
      getCitiesData(selectedRegion.province.id);
    } else {
      setCities([]);
    }
  }, [selectedRegion.province]);

  useEffect(() => {
    if (selectedRegion.city) {
      getJurisdictionsData(selectedRegion.city.id);
    } else {
      setJurisdictions([]);
    }
  }, [selectedRegion.city]);

  return {
    provincesLoading: isProvincesLoading,
    citiesLoading: isCitiesLoading,
    jurisdictionsLoading: isJurisdictionsLoading,
    availableProvinces: provinces,
    availableCities: cities,
    availableJurisdictions: jurisdictions,
    selectedRegion,
    setSelectedRegion,
  };
}

const getAllProvinceWithChild = async () => {
  const res = await netCall<TProvince[]>({
    method: "GET",
    endPoint: `${ManagementApiUrl()}/api/Province/GetAllProvinceWithChilds`,
  });
  if (res.responseCode === TResponseCode.OK) {
    return res.results || [];
  } else {
    return undefined;
  }
};

const getProvinceList = async () => {
  const res = await netCall<TProvince[]>({
    method: "POST",
    endPoint: `${ManagementApiUrl()}/api/Province/GetAllProvince`,
  });
  if (res.responseCode === TResponseCode.OK) {
    return res.results || [];
  } else {
    return undefined;
  }
};
const getCitiesList = async (provinceId: string) => {
  const res = await netCall<TProvince>({
    method: "GET",
    endPoint: `${ManagementApiUrl()}/api/Province/GetProvince/${provinceId}`,
  });
  if (res.responseCode === TResponseCode.OK) {
    return res.results.cities || [];
  } else {
    return undefined;
  }
};
const getJurisdictionsList = async (cityId: string) => {
  const res = await netCall<TCity>({
    method: "GET",
    endPoint: `${ManagementApiUrl()}/api/City/GetCity/${cityId}`,
  });
  if (res.responseCode === TResponseCode.OK) {
    return res?.results?.jurisdictions || [];
  } else {
    return undefined;
  }
};
